import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { ProductEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { getChangedFields } from "./shared/changedFields";
import { ChangedFieldsList } from "./shared/ChangedFieldsList";
import { createProductLink, findEventProduct, getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type ProductChangedEvent = Extract<ProductEvents, { eventTypeName: "ProductEvent.Changed" }>;
const renderProductChanged = (props: RendererProps<ProductChangedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const eventColors = getEventColors(theme);
    const actorTitle = getActorTitle(event);

    const actorLabel = actorTitle ?? i18next.t("common:status.unknown");
    const changedFields = event.data ? getChangedFields(event.data) : [];

    const product = findEventProduct(event, context);
    const productName = product?.customBuildConfig?.productName;

    const describe = () => {
        if (!product || !productName) {
            return i18next.t("modals:eventModals.productChangedDescription", {
                actorTitle: actorLabel,
                entityId: event.entityId,
                count: changedFields.length || undefined,
            });
        }

        const components = { productLink: createProductLink(product, context) };
        const values = { actorTitle: actorLabel, productName };

        if (!changedFields.length) {
            return (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.productChangedDescriptionWithNameWithoutData"
                    values={values}
                    components={components}
                />
            );
        }

        return (
            <Trans
                ns="modals"
                i18nKey="eventModals.productChangedDescriptionWithName"
                count={changedFields.length}
                values={values}
                components={components}
            />
        );
    };

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.productChangedTitle"),
        component: describe(),
        details: changedFields.length ? <ChangedFieldsList fields={changedFields} /> : undefined,
    };
};

type ProductDeletedEvent = Extract<ProductEvents, { eventTypeName: "ProductEvent.Deleted" }>;
const renderProductDeleted = (props: RendererProps<ProductDeletedEvent>): EventContentResult | null => {
    const { event, theme } = props;
    const eventColors = getEventColors(theme);
    const actorTitle = getActorTitle(event);

    const actorLabel = actorTitle ?? i18next.t("common:status.unknown");

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.productDeletedTitle"),
        component: i18next.t("modals:eventModals.productDeletedDescription", {
            actorTitle: actorLabel,
            entityId: event.entityId,
        }),
    };
};

export const renderProductEventContent = (
    event: ProductEvents,
    context: EventContext,
    theme: Theme,
): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "ProductEvent.Changed":
            return renderProductChanged({ event, context, theme });
        case "ProductEvent.Deleted":
            return renderProductDeleted({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
